/* Cabentra — behaviour for the added sections.
   Vanilla only, no framework, no build step. Everything here is defensive:
   if a section is not on the page, its module simply does nothing.
   Each module re-runs on the theme editor's section events so a merchant
   sees live behaviour while editing. */

(function () {
  'use strict';

  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)');

  function each(selector, root, fn) {
    var scope = root && root.querySelectorAll ? root : document;
    Array.prototype.forEach.call(scope.querySelectorAll(selector), fn);
  }

  /* ---- Slideshow ------------------------------------------------------ */

  function initSlideshow(frame) {
    if (frame.dataset.ready === 'true') return;
    frame.dataset.ready = 'true';

    var slides = Array.prototype.slice.call(frame.querySelectorAll('[data-slide]'));
    if (slides.length < 2) return;

    var dots = Array.prototype.slice.call(frame.querySelectorAll('[data-slide-to]'));
    var pause = frame.querySelector('[data-slide-pause]');
    var autoplay = frame.getAttribute('data-autoplay') === 'true' && !reduced.matches;
    var interval = parseInt(frame.getAttribute('data-interval'), 10) || 6000;
    var index = 0;
    var timer = null;

    function show(next) {
      index = (next + slides.length) % slides.length;
      slides.forEach(function (slide, i) {
        if (i === index) {
          slide.setAttribute('data-active', '');
        } else {
          slide.removeAttribute('data-active');
        }
      });
      dots.forEach(function (dot, i) {
        dot.setAttribute('aria-selected', i === index ? 'true' : 'false');
      });
    }

    function start() {
      if (!autoplay || timer) return;
      timer = window.setInterval(function () { show(index + 1); }, interval);
      frame.setAttribute('data-paused', 'false');
    }

    function stop(markPaused) {
      if (timer) { window.clearInterval(timer); timer = null; }
      if (markPaused) frame.setAttribute('data-paused', 'true');
    }

    var prev = frame.querySelector('[data-slide-prev]');
    var next = frame.querySelector('[data-slide-next]');
    if (prev) prev.addEventListener('click', function () { stop(true); show(index - 1); });
    if (next) next.addEventListener('click', function () { stop(true); show(index + 1); });

    dots.forEach(function (dot) {
      dot.addEventListener('click', function () {
        stop(true);
        show(parseInt(dot.getAttribute('data-slide-to'), 10) || 0);
      });
    });

    if (pause) {
      pause.addEventListener('click', function () {
        if (timer) {
          stop(true);
          pause.setAttribute('aria-label', pause.getAttribute('data-label-play') || 'Play');
        } else {
          start();
          pause.setAttribute('aria-label', pause.getAttribute('data-label-pause') || 'Pause');
        }
      });
    }

    /* Movement stops while someone is reading or tabbing through it. */
    frame.addEventListener('mouseenter', function () { if (timer) stop(false); });
    frame.addEventListener('mouseleave', function () { if (!frame.getAttribute('data-paused') || frame.getAttribute('data-paused') === 'false') start(); });
    frame.addEventListener('focusin', function () { if (timer) stop(false); });

    frame.addEventListener('keydown', function (event) {
      if (event.key === 'ArrowLeft') { stop(true); show(index - 1); }
      if (event.key === 'ArrowRight') { stop(true); show(index + 1); }
    });

    show(0);
    start();
  }

  /* ---- Gallery lightbox ----------------------------------------------- */

  var lightbox = null;
  var lightboxReturn = null;

  function buildLightbox() {
    if (lightbox) return lightbox;

    lightbox = document.createElement('div');
    lightbox.className = 'cb-lightbox';
    lightbox.setAttribute('role', 'dialog');
    lightbox.setAttribute('aria-modal', 'true');
    lightbox.hidden = true;
    lightbox.innerHTML =
      '<button class="cb-lightbox__close" type="button" data-lightbox-close aria-label="Close">' +
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>' +
      '</button>' +
      '<figure class="cb-lightbox__figure"><img class="cb-lightbox__img" alt=""><figcaption class="cb-lightbox__cap t-small"></figcaption></figure>';

    document.body.appendChild(lightbox);

    lightbox.addEventListener('click', function (event) {
      if (event.target.closest('[data-lightbox-close]') || event.target === lightbox) closeLightbox();
    });

    document.addEventListener('keydown', function (event) {
      if (lightbox.hidden) return;
      if (event.key === 'Escape') closeLightbox();
      if (event.key === 'Tab') {
        var focusable = lightbox.querySelectorAll('button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])');
        if (!focusable.length) return;
        var first = focusable[0];
        var last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    });

    return lightbox;
  }

  function openLightbox(trigger) {
    var box = buildLightbox();
    var img = box.querySelector('.cb-lightbox__img');
    var cap = box.querySelector('.cb-lightbox__cap');
    var source = trigger.querySelector('img');

    img.src = trigger.getAttribute('data-full') || (source ? source.src : '');
    img.alt = source ? source.alt : '';
    cap.textContent = trigger.getAttribute('data-caption') || '';
    cap.hidden = cap.textContent === '';

    lightboxReturn = trigger;
    box.hidden = false;
    document.documentElement.style.overflow = 'hidden';
    var close = box.querySelector('[data-lightbox-close]');
    if (close) close.focus();
  }

  function closeLightbox() {
    if (!lightbox) return;
    lightbox.hidden = true;
    document.documentElement.style.overflow = '';
    if (lightboxReturn && document.contains(lightboxReturn)) lightboxReturn.focus();
    lightboxReturn = null;
  }

  document.addEventListener('click', function (event) {
    var trigger = event.target.closest('[data-lightbox-open]');
    if (trigger) openLightbox(trigger);
  });

  /* ---- Before and after ------------------------------------------------ */

  function initBeforeAfter(media) {
    if (media.dataset.ready === 'true') return;
    media.dataset.ready = 'true';

    var range = media.querySelector('[data-before-after-range]');
    if (!range) return;

    function apply() {
      media.style.setProperty('--pos', range.value + '%');
    }

    range.addEventListener('input', apply);
    range.addEventListener('change', apply);

    /* Dragging anywhere on the image feels more natural than only on the
       handle, so pointer position is mapped onto the same range value. */
    var dragging = false;

    function fromPointer(event) {
      var rect = media.getBoundingClientRect();
      if (!rect.width) return;
      var pct = ((event.clientX - rect.left) / rect.width) * 100;
      range.value = Math.max(0, Math.min(100, Math.round(pct)));
      apply();
    }

    media.addEventListener('pointerdown', function (event) {
      dragging = true;
      if (media.setPointerCapture) {
        try { media.setPointerCapture(event.pointerId); } catch (e) { /* not fatal */ }
      }
      fromPointer(event);
    });
    media.addEventListener('pointermove', function (event) { if (dragging) fromPointer(event); });
    media.addEventListener('pointerup', function () { dragging = false; });
    media.addEventListener('pointercancel', function () { dragging = false; });

    apply();
  }

  /* ---- Counting figures ------------------------------------------------ */

  function formatNumber(value, decimals, separator) {
    var fixed = value.toFixed(decimals);
    if (!separator) return fixed;
    var parts = fixed.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return parts.join('.');
  }

  function runCounters(group) {
    var duration = parseInt(group.getAttribute('data-duration'), 10) || 1600;

    each('[data-counter]', group, function (el) {
      if (el.dataset.counted === 'true' || el.offsetParent === null) return;
      var raw = el.getAttribute('data-to') || '';

      /* A range is not a figure. Stripping the punctuation out of "3-4" glues
         the halves into "34", which then cheerfully counts up to thirty-four -
         a wrong number presented with total confidence. En and em dashes are
         not the ASCII hyphen the character class keeps, which is how this got
         through. Leave any range exactly as it was typed. */
      if (/\d\s*[\u2010-\u2015-]\s*\d/.test(raw)) return;

      var target = parseFloat(raw.replace(/[^0-9.\-]/g, ''));
      var decimals = parseInt(el.getAttribute('data-decimals'), 10) || 0;
      var separator = el.getAttribute('data-separator') === 'true';

      if (isNaN(target)) return;
      el.dataset.counted = 'true';

      if (reduced.matches) {
        el.textContent = formatNumber(target, decimals, separator);
        return;
      }

      var startedAt = null;

      function step(now) {
        if (startedAt === null) startedAt = now;
        var progress = Math.min((now - startedAt) / duration, 1);
        var eased = 1 - Math.pow(1 - progress, 3);
        el.textContent = formatNumber(target * eased, decimals, separator);
        if (progress < 1) window.requestAnimationFrame(step);
      }

      el.textContent = formatNumber(0, decimals, separator);
      window.requestAnimationFrame(step);
    });
  }

  function initCounters(group) {
    if (group.dataset.ready === 'true') return;
    group.dataset.ready = 'true';

    if (group.getAttribute('data-animate-on-scroll') === 'false') {
      group.dataset.counterActive = 'true';
      runCounters(group);
      return;
    }

    if (!('IntersectionObserver' in window)) {
      group.dataset.counterActive = 'true';
      runCounters(group);
      return;
    }

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        group.dataset.counterActive = 'true';
        runCounters(group);
        observer.disconnect();
      });
    }, { rootMargin: '0px 0px -15% 0px' });

    observer.observe(group);
  }

  document.addEventListener('cabentra:audience-change', function () {
    each('[data-counters]', document, function (group) {
      if (group.dataset.counterActive === 'true') runCounters(group);
    });
  });

  /* ---- Countdown ------------------------------------------------------- */

  function initCountdown(panel) {
    if (panel.dataset.ready === 'true') return;
    panel.dataset.ready = 'true';

    var deadline = new Date(panel.getAttribute('data-deadline'));
    var clock = panel.querySelector('[data-countdown-clock]');
    var ended = panel.querySelector('[data-countdown-ended]');
    var onEnd = panel.getAttribute('data-on-end') || 'message';
    var section = panel.closest('.cdown');

    if (isNaN(deadline.getTime())) {
      if (clock) clock.hidden = true;
      return;
    }

    var fields = {
      days: panel.querySelector('[data-countdown-days]'),
      hours: panel.querySelector('[data-countdown-hours]'),
      minutes: panel.querySelector('[data-countdown-minutes]'),
      seconds: panel.querySelector('[data-countdown-seconds]')
    };

    function pad(value) { return value < 10 ? '0' + value : String(value); }

    function finish() {
      if (onEnd === 'hide' && section) {
        section.hidden = true;
        return;
      }
      if (clock) clock.hidden = true;
      if (ended) ended.hidden = false;
    }

    function tick() {
      var remaining = deadline.getTime() - Date.now();
      if (remaining <= 0) {
        finish();
        window.clearInterval(timer);
        return;
      }

      var seconds = Math.floor(remaining / 1000);
      var days = Math.floor(seconds / 86400);
      var hours = Math.floor((seconds % 86400) / 3600);
      var minutes = Math.floor((seconds % 3600) / 60);
      var secs = seconds % 60;

      if (fields.days) fields.days.textContent = pad(days);
      if (fields.hours) fields.hours.textContent = pad(hours);
      if (fields.minutes) fields.minutes.textContent = pad(minutes);
      if (fields.seconds) fields.seconds.textContent = pad(secs);
    }

    tick();
    var timer = window.setInterval(tick, 1000);
  }

  /* ---- Logo marquee ---------------------------------------------------- */

  function initMarquee(viewport) {
    if (viewport.dataset.ready === 'true') return;
    viewport.dataset.ready = 'true';

    if (viewport.getAttribute('data-pause-on-hover') !== 'true') return;

    viewport.addEventListener('mouseenter', function () { viewport.setAttribute('data-paused', 'true'); });
    viewport.addEventListener('mouseleave', function () { viewport.setAttribute('data-paused', 'false'); });
    viewport.addEventListener('focusin', function () { viewport.setAttribute('data-paused', 'true'); });
    viewport.addEventListener('focusout', function () { viewport.setAttribute('data-paused', 'false'); });
  }

  /* ---- Related products ------------------------------------------------
     Shopify renders the recommendations itself; the page only asks for the
     same section back with the recommendation context attached. */

  function initRelated(root) {
    if (root.dataset.ready === 'true') return;
    root.dataset.ready = 'true';

    var url = root.getAttribute('data-url');
    if (!url || root.children.length > 0) return;

    var section = root.closest('.related');

    fetch(url)
      .then(function (response) {
        if (!response.ok) throw new Error('Recommendations request failed: ' + response.status);
        return response.text();
      })
      .then(function (html) {
        var parsed = new DOMParser().parseFromString(html, 'text/html');
        var fresh = parsed.querySelector('[data-related-products]');
        if (fresh && fresh.children.length > 0) {
          root.innerHTML = fresh.innerHTML;
        } else if (section) {
          section.hidden = true;
        }
      })
      .catch(function (error) {
        console.error(error);
        if (section) section.hidden = true;
      });
  }

  /* ---- Recently viewed -------------------------------------------------
     Handles live in this browser only. Each card is fetched from its own
     product page so the card markup stays in Liquid rather than JS. */

  var RV_KEY = 'cabentra:recently-viewed';

  function rvRead() {
    try {
      var stored = JSON.parse(window.localStorage.getItem(RV_KEY));
      return Array.isArray(stored) ? stored : [];
    } catch (error) {
      return [];
    }
  }

  function rvWrite(list) {
    try {
      window.localStorage.setItem(RV_KEY, JSON.stringify(list.slice(0, 20)));
    } catch (error) { /* private browsing, nothing to do */ }
  }

  function initRecentlyViewed(root) {
    if (root.dataset.ready === 'true') return;
    root.dataset.ready = 'true';

    var list = root.querySelector('[data-recently-viewed-list]');
    var clear = root.querySelector('[data-recently-viewed-clear]');
    var current = root.getAttribute('data-current');
    var limit = parseInt(root.getAttribute('data-limit'), 10) || 4;
    var base = (root.getAttribute('data-root') || '/').replace(/\/$/, '');

    var handles = rvRead();

    var others = handles.filter(function (handle) { return handle !== current; });

    if (current) {
      rvWrite([current].concat(others));
    }

    if (clear) {
      clear.addEventListener('click', function () {
        rvWrite([]);
        root.hidden = true;
      });
    }

    var wanted = others.slice(0, limit);
    if (!list || wanted.length === 0) return;

    var requests = wanted.map(function (handle) {
      return fetch(base + '/products/' + encodeURIComponent(handle) + '?section_id=recently-viewed-card')
        .then(function (response) { return response.ok ? response.text() : ''; })
        .catch(function () { return ''; });
    });

    Promise.all(requests).then(function (results) {
      var shown = 0;

      results.forEach(function (html) {
        if (!html) return;
        var parsed = new DOMParser().parseFromString(html, 'text/html');
        var card = parsed.querySelector('.card');
        if (!card) return;
        var item = document.createElement('li');
        item.appendChild(card);
        list.appendChild(item);
        shown += 1;
      });

      if (shown > 0) root.hidden = false;
    });
  }

  /* ---- Sticky add to cart ----------------------------------------------
     The bar owns no form of its own. It submits the real product form, so
     variant state and the quote-only property are never duplicated. */

  function initStickyAtc(bar) {
    if (bar.dataset.ready === 'true') return;
    bar.dataset.ready = 'true';

    var form = document.querySelector('form[data-add-to-spec]');
    var anchor = document.querySelector(bar.getAttribute('data-anchor') || '.product-form') || form;
    var button = bar.querySelector('[data-sticky-add]');
    var mainButton = document.querySelector('.product-form__submit');
    var priceOut = bar.querySelector('[data-sticky-price]');
    var priceIn = document.querySelector('[data-product-price]');

    if (!form || !anchor) return;

    bar.hidden = false;

    if (button) {
      button.addEventListener('click', function () {
        if (typeof form.requestSubmit === 'function') {
          form.requestSubmit();
        } else {
          form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
        }
      });
    }

    if (button && mainButton && bar.getAttribute('data-match-label') === 'true') {
      var syncLabel = function () {
        button.textContent = mainButton.textContent.trim();
        button.disabled = mainButton.disabled;
      };
      syncLabel();
      new MutationObserver(syncLabel).observe(mainButton, {
        childList: true, characterData: true, subtree: true, attributes: true, attributeFilter: ['disabled']
      });
    }

    if (priceOut && priceIn) {
      /* Copy the markup, not the text: the price holds an inc-VAT and an ex-VAT figure and CSS shows one. Flattening it to text printed both. */
      var syncPrice = function () { priceOut.innerHTML = priceIn.innerHTML; };
      new MutationObserver(syncPrice).observe(priceIn, { childList: true, characterData: true, subtree: true });
    }

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          var past = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          bar.setAttribute('data-shown', past ? 'true' : 'false');
        });
      }, { threshold: 0 }).observe(anchor);
    } else {
      bar.setAttribute('data-shown', 'true');
    }
  }

  /* ---- Back to top ----------------------------------------------------- */

  function initBackToTop() {
    var button = document.querySelector('[data-back-to-top]');
    if (!button || button.dataset.ready === 'true') return;
    button.dataset.ready = 'true';

    var threshold = parseInt(button.getAttribute('data-threshold'), 10) || 600;
    button.hidden = false;

    function update() {
      button.setAttribute('data-shown', window.scrollY > threshold ? 'true' : 'false');
    }

    button.addEventListener('click', function () {
      window.scrollTo({ top: 0, behavior: reduced.matches ? 'auto' : 'smooth' });
      var main = document.getElementById('main');
      if (main) main.focus();
    });

    window.addEventListener('scroll', update, { passive: true });
    update();
  }

  /* ---- Wiring ---------------------------------------------------------- */

  function initAll(root) {
    each('[data-slideshow]', root, initSlideshow);
    each('[data-before-after]', root, initBeforeAfter);
    each('[data-counters]', root, initCounters);
    each('[data-countdown]', root, initCountdown);
    each('[data-marquee]', root, initMarquee);
    each('[data-related-products]', root, initRelated);
    each('[data-recently-viewed]', root, initRecentlyViewed);
    each('[data-sticky-atc]', root, initStickyAtc);
    initBackToTop();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  } else {
    initAll(document);
  }

  /* Theme editor: sections are re-rendered in place, so re-run on the
     section that changed rather than the whole document. */
  document.addEventListener('shopify:section:load', function (event) { initAll(event.target); });
  document.addEventListener('shopify:section:select', function (event) { initAll(event.target); });
})();
